import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import type { CartFreightState } from "@/hooks/checkout/use-cart-freight-quote";
import { formatCents } from "@/lib/formatting/currency";
import { checkoutDeliveryCents } from "@shared/orders/checkout-quote-summary";

interface CheckoutSummaryRowsProps {
  itemCount: number;
  goodsCents: number;
  totalCents: number;
  freight: CartFreightState;
  complete: boolean;
  loadCount: number;
  compact?: boolean;
}

export function CheckoutSummaryRows({
  itemCount,
  goodsCents,
  totalCents,
  freight,
  complete,
  loadCount,
  compact = false,
}: CheckoutSummaryRowsProps) {
  const quoted = complete && freight.quote !== null && !freight.loading;
  return (
    <>
      <dl className={compact ? "space-y-2 text-sm" : "mt-4 space-y-3 text-sm"}>
        <Row label={`${itemCount} ${itemCount === 1 ? "product" : "products"}`}>
          {formatCents(goodsCents)}
        </Row>
        <Row label={loadCount > 1 ? `Delivery (${loadCount} trucks)` : "Delivery"}>
          <FreightAmount freight={freight} complete={complete} />
        </Row>
        {freight.quote?.liftGateCents ? (
          <Row label="Lift gate">{formatCents(freight.quote.liftGateCents)}</Row>
        ) : null}
        <Row label="Tax">
          <span className="text-muted-foreground">Shown at payment</span>
        </Row>
      </dl>
      <Separator className={compact ? "my-3" : "my-4"} />
      <div className="flex justify-between font-semibold">
        <span>Total before tax</span>
        <span className="tabular-nums">
          {quoted ? formatCents(totalCents) : "—"}
        </span>
      </div>
    </>
  );
}

function Row({
  label,
  children,
}: {
  label: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="flex justify-between gap-3">
      <dt>{label}</dt>
      <dd className="text-right tabular-nums">{children}</dd>
    </div>
  );
}

function FreightAmount({
  freight,
  complete,
}: {
  freight: CartFreightState;
  complete: boolean;
}) {
  if (freight.loading) return <Skeleton className="ml-auto h-4 w-16" />;
  if (freight.error)
    return <span className="text-destructive-strong">Couldn&apos;t quote</span>;
  if (!freight.quote || !complete)
    return <span className="text-muted-foreground">Add delivery details</span>;
  return <>{formatCents(checkoutDeliveryCents(freight.quote))}</>;
}
